import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import {
  Card,
  CardHeader,
  CardBody,
  Badge,
  Button,
  Spinner,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Alert,
} from '@/components/ui';
import type { Owner, Parcel } from '@/types';

export default function OwnerDetailPage() {
  const { id } = useParams<{ id: string }>();

  const { data: owner, isLoading: ownerLoading } = useQuery<Owner>({
    queryKey: ['owner', id],
    queryFn: () => api.getOwner(id!),
    enabled: !!id,
  });

  const { data: parcels, isLoading: parcelsLoading } = useQuery<Parcel[]>({
    queryKey: ['owner', id, 'parcels'],
    queryFn: () => api.getOwnerParcels(id!),
    enabled: !!id,
  });

  if (ownerLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!owner) {
    return (
      <Alert variant="error" title="Owner not found">
        The owner you are looking for does not exist.
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Link to="/search" className="text-sm text-gray-500 hover:text-gray-700 mb-2 inline-block">
            ← Back to Search
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">{owner.name}</h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant={owner.entity_type === 'company' ? 'warning' : 'default'}>
              {owner.entity_type === 'company' ? 'Company' : 'Individual'}
            </Badge>
            <span className="text-gray-500">
              {parcels?.length || 0} properties
            </span>
          </div>
        </div>
      </div>

      {/* Owner Info */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <h3 className="font-semibold">Owner Details</h3>
          </CardHeader>
          <CardBody>
            <dl className="space-y-4">
              <div>
                <dt className="text-sm text-gray-500">Legal Name</dt>
                <dd className="font-medium">{owner.name}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Entity Type</dt>
                <dd>
                  <Badge variant={owner.entity_type === 'company' ? 'warning' : 'default'}>
                    {owner.entity_type}
                  </Badge>
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Confidence</dt>
                <dd>{Math.round(owner.confidence * 100)}%</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Record Created</dt>
                <dd>{new Date(owner.created_at).toLocaleDateString()}</dd>
              </div>
            </dl>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <h3 className="font-semibold">Mailing Address</h3>
          </CardHeader>
          <CardBody>
            {owner.mailing_address ? (
              <address className="not-italic">
                <p>{owner.name}</p>
                <p>{owner.mailing_address}</p>
                <p>
                  {owner.mailing_city}, {owner.mailing_state} {owner.mailing_zip}
                </p>
              </address>
            ) : (
              <p className="text-gray-500">No mailing address on file</p>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Properties */}
      <Card>
        <CardHeader>
          <h3 className="font-semibold">Properties Owned</h3>
        </CardHeader>
        <CardBody className="p-0">
          {parcelsLoading ? (
            <div className="flex items-center justify-center h-32">
              <Spinner size="md" />
            </div>
          ) : parcels && parcels.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Address</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead>County</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>APN</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {parcels.map((parcel) => (
                  <TableRow key={parcel.id}>
                    <TableCell>
                      <Link
                        to={`/parcels/${parcel.id}`}
                        className="text-primary-600 hover:underline"
                      >
                        {parcel.address}
                      </Link>
                    </TableCell>
                    <TableCell>{parcel.city || '-'}</TableCell>
                    <TableCell>{parcel.county}</TableCell>
                    <TableCell>
                      <Badge variant="info">{parcel.state}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{parcel.apn || '-'}</TableCell>
                    <TableCell>
                      <Link to={`/parcels/${parcel.id}`}>
                        <Button variant="ghost" size="sm">View</Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12 text-gray-500">
              No properties found for this owner
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
